import { ApolloClient, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { inject, injectable } from 'inversify';
import { ConfigurationService } from '../ConfigurationService/ConfigurationService';
import DependencyType from '../../dependancyInjection/DependencyType';
import {
    AbortPaymentRequestDocument,
    AbortPaymentRequestMutation,
    AbortPaymentRequestMutationVariables,
    CreatePaidOrderDocument,
    CreatePaidOrderInput,
    CreatePaidOrderMutation,
    CreatePaidOrderMutationFn,
    CreatePaidOrderMutationVariables,
    GetPaymentRequestDocument,
    GetPaymentRequestQuery,
    GetPaymentRequestQueryVariables,
    GetPaymentTokenDocument,
    GetPaymentTokenQuery,
    GetPaymentTokenQueryVariables,
    PaymentGenericPayload,
    StartPaymentRequestDocument,
    StartPaymentRequestMutation,
    StartPaymentRequestMutationVariables,
} from '../../provider/cloudshelf/graphql/generated/cloudshelf_types';

export type GetPaymentTokenResponse = {
    success: boolean;
    token?: string;
    message?: string;
};

@injectable()
export class PaymentDemoService {
    constructor(
        @inject(DependencyType.ApolloClient) private readonly apolloClient: ApolloClient<NormalizedCacheObject>,
        @inject(DependencyType.ConfigurationService) private readonly configService: ConfigurationService,
    ) {}
    //

    async getPaymentToken(password: string): Promise<GetPaymentTokenResponse> {
        const { data, errors, error } = await this.apolloClient.query<
            GetPaymentTokenQuery,
            GetPaymentTokenQueryVariables
        >({
            query: GetPaymentTokenDocument,
            variables: {
                organisationId: this.configService.config()?.ownerId ?? '',
                password,
            },
            fetchPolicy: 'no-cache',
        });

        if (!!error || (errors && Array.isArray(errors)) || !data.getPaymentToken || !data.getPaymentToken.success) {
            console.log('errors', errors, error);
            console.log('data', data);
            return {
                success: false,
                token: undefined,
                message: data.getPaymentToken.message ?? 'Failed to get payment token',
            };
        }

        return {
            success: data.getPaymentToken.success,
            token: data.getPaymentToken.token ?? undefined,
            message: data.getPaymentToken.message ?? undefined,
        };
    }

    async startPaymentRequest(
        sessionId: string,
        token: string,
        amount: number,
        currencyCode: string,
    ): Promise<PaymentGenericPayload> {
        console.log('ammount', amount);
        const { data, errors } = await this.apolloClient.mutate<
            StartPaymentRequestMutation,
            StartPaymentRequestMutationVariables
        >({
            mutation: StartPaymentRequestDocument,
            variables: {
                sessionId,
                amount,
                token,
                organisationId: this.configService.config()?.ownerId ?? '',
                currencyCode,
            },
            fetchPolicy: 'no-cache',
        });

        if ((errors && Array.isArray(errors)) || !data?.startPaymentRequest || !data.startPaymentRequest.success) {
            return {
                success: false,
                message: data?.startPaymentRequest.message ?? 'Failed to start payment request',
            };
        }

        return {
            success: data.startPaymentRequest.success,
            message: data.startPaymentRequest.message,
        };
    }

    async abortPaymentRequest(sessionId: string, token: string): Promise<PaymentGenericPayload> {
        const { data, errors } = await this.apolloClient.mutate<
            AbortPaymentRequestMutation,
            AbortPaymentRequestMutationVariables
        >({
            mutation: AbortPaymentRequestDocument,
            variables: {
                sessionId,
                token,
            },
            fetchPolicy: 'no-cache',
        });

        if ((errors && Array.isArray(errors)) || !data?.abortPaymentRequest || !data.abortPaymentRequest.success) {
            return {
                success: false,
                message: data?.abortPaymentRequest.message ?? 'Failed to abort payment request',
            };
        }

        return {
            success: data.abortPaymentRequest.success,
            message: data.abortPaymentRequest.message,
        };
    }

    async getPaymentRequest(sessionId: string, token: string): Promise<PaymentGenericPayload> {
        const { data, errors, error } = await this.apolloClient.query<
            GetPaymentRequestQuery,
            GetPaymentRequestQueryVariables
        >({
            query: GetPaymentRequestDocument,
            variables: {
                sessionId,

                token,
            },
            fetchPolicy: 'no-cache',
        });

        if (!!error || (errors && Array.isArray(errors)) || !data?.getPaymentRequest || !data.getPaymentRequest) {
            return {
                success: false,
                message: data.getPaymentRequest.message ?? 'Failed to get payment request',
            };
        }

        return data.getPaymentRequest;
    }

    async createPaidOrder(
        basketId: string,
        sessionId: string,
        token: string,
        emailAddress: string,
    ): Promise<PaymentGenericPayload> {
        const input: CreatePaidOrderInput = {
            basketId,
            sessionId,
            token,
            emailAddress,
        };

        const { data, errors } = await this.apolloClient.mutate<
            CreatePaidOrderMutation,
            CreatePaidOrderMutationVariables
        >({
            mutation: CreatePaidOrderDocument,
            variables: {
                input,
            },
            fetchPolicy: 'no-cache',
        });

        if ((errors && Array.isArray(errors)) || !data?.createPaidOrder || !data.createPaidOrder.success) {
            return {
                success: false,
                message: data?.createPaidOrder.message ?? 'Failed to create paid order',
            };
        }

        return {
            success: data.createPaidOrder.success,
            message: data.createPaidOrder.message,
        };
    }
}
